import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { $createParagraphNode, $getRoot, $getSelection, $insertNodes, $isRangeSelection, CLEAR_EDITOR_COMMAND } from 'lexical';
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { $isRootTextContentEmptyCurry } from '@lexical/text';

export function useCallbackRef<T extends (...args: any[]) => any>(callback: T | undefined): T {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  return useMemo(() => ((...args) => callbackRef.current?.(...args)) as T, []);
}

export interface UseDebouncedCallbackOptions {
  delay: number;
  flushOnUnmount?: boolean;
  leading?: boolean;
}

export type UseDebouncedCallbackReturnValue<T extends (...args: any[]) => any> = ((
  ...args: Parameters<T>
) => void) & { flush: () => void; cancel: () => void };

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  options: number | UseDebouncedCallbackOptions
) {
  const { delay, flushOnUnmount, leading } =
    typeof options === 'number'
      ? { delay: options, flushOnUnmount: false, leading: false }
      : options;

  const handleCallback = useCallbackRef(callback);
  const debounceTimerRef = useRef(0);

  const lastCallback = useMemo(() => {
    const currentCallback = Object.assign(
      (...args: Parameters<T>) => {
        window.clearTimeout(debounceTimerRef.current);

        const isFirstCall = currentCallback._isFirstCall;
        currentCallback._isFirstCall = false;

        function clearTimeoutAndLeadingRef() {
          window.clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = 0;
          currentCallback._isFirstCall = true;
        }

        if (leading && isFirstCall) {
          handleCallback(...args);

          const resetLeadingState = () => {
            clearTimeoutAndLeadingRef();
          };

          const flush = () => {
            if (debounceTimerRef.current !== 0) {
              clearTimeoutAndLeadingRef();
              handleCallback(...args);
            }
          };

          const cancel = () => {
            clearTimeoutAndLeadingRef();
          };

          currentCallback.flush = flush;
          currentCallback.cancel = cancel;
          debounceTimerRef.current = window.setTimeout(resetLeadingState, delay);
          return;
        }

        if (leading && !isFirstCall) {
          const flush = () => {
            if (debounceTimerRef.current !== 0) {
              clearTimeoutAndLeadingRef();
              handleCallback(...args);
            }
          };

          const cancel = () => {
            clearTimeoutAndLeadingRef();
          };

          currentCallback.flush = flush;
          currentCallback.cancel = cancel;

          const resetLeadingState = () => {
            clearTimeoutAndLeadingRef();
          };
          debounceTimerRef.current = window.setTimeout(resetLeadingState, delay);
          return;
        }

        const flush = () => {
          if (debounceTimerRef.current !== 0) {
            clearTimeoutAndLeadingRef();
            handleCallback(...args);
          }
        };

        const cancel = () => {
          clearTimeoutAndLeadingRef();
        };

        currentCallback.flush = flush;
        currentCallback.cancel = cancel;
        debounceTimerRef.current = window.setTimeout(flush, delay);
      },
      {
        flush: () => { },
        cancel: () => { },
        _isFirstCall: true,
      }
    );
    return currentCallback;
  }, [handleCallback, delay, leading]);

  useEffect(
    () => () => {
      if (flushOnUnmount) {
        lastCallback.flush();
      } else {
        lastCallback.cancel();
      }
    },
    [lastCallback, flushOnUnmount]
  );

  return lastCallback;
}

export default function StatePlugin(props: { html?: string, id: string, onChange: (content: string) => void, autosave: (content: string) => void }) {
  const [editor] = useLexicalComposerContext()
  const throttledAutosave = useDebouncedCallback(props.autosave, { delay: 1000, flushOnUnmount: false });

  useEffect(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(props.html || "", "text/html");
    editor.update(() => {
      let nodes = $generateNodesFromDOM(editor, dom);
      let isEmpty = editor
        .getEditorState()
        .read($isRootTextContentEmptyCurry(editor.isComposing(), true));
      if (!isEmpty) {
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      } else {
        $insertNodes(nodes);
      }
    })
  }, [props.html, editor, props.id])

  return <OnChangePlugin
    ignoreSelectionChange={true}
    ignoreHistoryMergeTagChange={true}
    onChange={(editorState, editor, tags) => {
      try {
        editorState.read(() => {
          let curr = $generateHtmlFromNodes(editor);
          props.onChange(curr);
          throttledAutosave(curr);
        })
      } catch (e) {
        if (window["__airbrakeClient"]) {
          window["__airbrakeClient"].notify({ error: e, params: {context: "In StatePlugin onChange"} });
        }
        
        throw e;
      }
    }} />
}